import { FETCH_HUB_SOLUTIONS } from "@apiUtils";
import { getHubCompanyId, mutationFetch } from "@atlas/sdk";
import type { IHubSubscribedSolution } from "@models";
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const HUB_SOLUTIONS_KEY = "hubSolutionsKey";

export const getHubSolutionsKey = (
  companyUuid: string,
  appMapId: string,
  companyId: number,
) => [HUB_SOLUTIONS_KEY, companyUuid, appMapId, companyId] as const;

function buildFetchHubSolutionsUrl(
  companyUuid: string,
  appMapId: string,
): string {
  return FETCH_HUB_SOLUTIONS.replace(
    "{companyUuid}",
    encodeURIComponent(companyUuid),
  ).replace("{appMapId}", encodeURIComponent(appMapId));
}

type HubSubApplicationItem = Partial<IHubSubscribedSolution> & {
  id?: string;
  applicationId?: string;
  applicationName?: string;
  applicationLabel?: string;
  applicationDescription?: string;
};

type HubSolutionsListPayload =
  | HubSubApplicationItem[]
  | {
      solutions?: HubSubApplicationItem[];
      data?: HubSubApplicationItem[];
      subApplications?: HubSubApplicationItem[];
    };

function mapSubApplicationToSolution(
  item: HubSubApplicationItem,
): IHubSubscribedSolution {
  const solutionId =
    item.solutionId?.trim() ||
    item.id?.trim() ||
    item.applicationId?.trim() ||
    "";
  const name =
    item.name?.trim() ||
    item.applicationName?.trim() ||
    item.label?.trim() ||
    item.applicationLabel?.trim() ||
    solutionId;
  const label =
    item.label?.trim() ||
    item.applicationLabel?.trim() ||
    item.name?.trim() ||
    item.applicationName?.trim() ||
    name;
  const description =
    item.description?.trim() || item.applicationDescription?.trim() || "";

  return {
    solutionId,
    name,
    label,
    description,
    version: item.version ?? 0,
    subscriptionStatus: item.subscriptionStatus,
    subscribeUserId: item.subscribeUserId,
    subscribeUserName: item.subscribeUserName,
    createdBy: item.createdBy,
    idpOutputEnabled: item.idpOutputEnabled ?? false,
    idpOutputBackupEnabled: item.idpOutputBackupEnabled ?? false,
    hasPhysicalAddOn: item.hasPhysicalAddOn ?? false,
    hasROTAddOn: item.hasROTAddOn ?? false,
    ocrEnabled: item.ocrEnabled ?? false,
  };
}

function normalizeHubSolutionsList(
  response: HubSolutionsListPayload | null | undefined,
): IHubSubscribedSolution[] {
  const items = Array.isArray(response)
    ? response
    : Array.isArray(response?.solutions)
      ? response.solutions
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.subApplications)
          ? response.subApplications
          : [];

  return items
    .map(mapSubApplicationToSolution)
    .filter((solution) => !!solution.solutionId);
}

export const useHubSolutions = (
  companyUuid: string,
  appMapId: string,
  options?: Partial<
    UseQueryOptions<
      IHubSubscribedSolution[],
      AxiosError,
      IHubSubscribedSolution[],
      ReturnType<typeof getHubSolutionsKey>
    >
  >,
) => {
  const companyId = getHubCompanyId() ?? 0;

  return useQuery({
    queryKey: getHubSolutionsKey(companyUuid, appMapId, companyId),
    queryFn: async () => {
      const response = await mutationFetch<HubSolutionsListPayload>({
        url: buildFetchHubSolutionsUrl(companyUuid, appMapId),
        method: "GET",
        headers: {
          companyid: companyId,
        },
      });
      return normalizeHubSolutionsList(response);
    },
    retry: false,
    ...options,
    enabled:
      (options?.enabled ?? true) &&
      !!companyUuid &&
      !!appMapId &&
      companyId > 0,
  });
};
